/**
 * The telemetry sub-flags as the capture sites read them: text, interaction, net,
 * diagnostics and friction, plus the P2 `visits` (visits and `tab.start`), `attention`
 * (attention spans) and `intent` (intents and their outcomes) groups.
 *
 * services/telemetry.js sets them from /api/me (`telemetryTextEnabled`, …); each is
 * false until the first answer and whenever the stream itself is off, so a capture
 * site that checks its group records nothing a disabled group would send.
 *
 * Before the first answer nobody knows yet: group events, and the legacy events the
 * text group supersedes, are queued (up to
 * `PRE_ANSWER_MAX`, with their capture time) and, at the answer, recorded when their
 * group is on and discarded otherwise. Events past the cap whose group turns out on
 * are reported as `telemetry.dropped {reason: "pre_enable"}` by the service.
 */
import { recordUiEvent, traceRing } from "../logRingBuffer";

/** Group names, in the order the plan enables them. */
export const TELEMETRY_GROUPS = Object.freeze([
  "text",
  "interaction",
  "net",
  "diagnostics",
  "visits",
  "attention",
  "friction",
  "intent",
]);

/** Longest captured text per field, in UTF-8 bytes. */
export const TEXT_MAX_BYTES = 8 * 1024;

/** Clip length of the legacy text fields while the text group is off. */
export const LEGACY_CLIP = 160;

/** Which group an event family belongs to; the trace-ring families plus the text group. */
const FAMILY_GROUP = {
  chat: "text",
  submit: "text",
  text: "text",
  action: "interaction",
  key: "interaction",
  scroll: "interaction",
  viewport: "interaction",
  net: "net",
  stream: "net",
  store: "diagnostics",
  console: "diagnostics",
  friction: "friction",
};

/**
 * The P2 families: recorded in the domain ring (200 entries, never evicted by trace
 * traffic, and in every bug bundle), each under its own group.
 */
const P2_FAMILY_GROUP = { visit: "visits", tab: "visits", attention: "attention" };

/** Events per minute across the groups before the excess is dropped. */
export const CLIENT_BUDGET_PER_MIN = (() => {
  const v = Number(import.meta.env?.VITE_TELEMETRY_BUDGET_PER_MIN);
  return Number.isFinite(v) && v > 0 ? v : 600;
})();

/**
 * A token bucket of `limit` events per minute; `take(family)` spends one, counting
 * the family when the bucket is empty.
 */
export function createBudget({ limit = CLIENT_BUDGET_PER_MIN, now = () => Date.now() } = {}) {
  let tokens = limit;
  let last = now();
  let dropped = {};
  return {
    take(family) {
      const t = now();
      tokens = Math.min(limit, tokens + ((t - last) * limit) / 60_000);
      last = t;
      if (tokens >= 1) {
        tokens -= 1;
        return true;
      }
      dropped[family] = (dropped[family] || 0) + 1;
      return false;
    },
    /** Dropped counts by family since the last call, or null. */
    takeDropped() {
      if (Object.keys(dropped).length === 0) return null;
      const out = dropped;
      dropped = {};
      return out;
    },
  };
}

let budget = createBudget();

/** Replace the budget (tests). */
export function setClientBudget(next) {
  budget = next;
}

/** The budget's dropped counts by family since the last call, or null (services/telemetry.js). */
export function takeBudgetDrops() {
  return budget.takeDropped();
}

const familyOf = (name) => (typeof name === "string" ? name.split(".", 1)[0] : "");

const OFF = Object.freeze({
  text: false,
  interaction: false,
  net: false,
  diagnostics: false,
  visits: false,
  attention: false,
  friction: false,
  intent: false,
});

let groups = OFF;

/** Most group events kept before the first /api/me answer. */
export const PRE_ANSWER_MAX = 200;

/** False until the first `setTelemetryGroups` call (the first /api/me answer). */
let answered = false;
/** Events captured before the answer: `{kind: "text" | "trace" | "legacy", event}`. */
let preAnswer = [];
/** Events past the pre-answer cap, counted by `kind` then family. */
let preAnswerOverflow = {};
/** Overflow whose group the answer turned on, pending `takePreAnswerDrops`. */
let preAnswerDropped = null;

function monoNow() {
  return typeof performance !== "undefined" ? performance.now() : null;
}

/** Hold a group event until the first answer; past the cap only its family is counted. */
function queuePreAnswer(kind, event) {
  if (preAnswer.length >= PRE_ANSWER_MAX) {
    const family = familyOf(event.event);
    const byFamily = (preAnswerOverflow[kind] ||= {});
    byFamily[family] = (byFamily[family] || 0) + 1;
    return;
  }
  const at = { captured_at: new Date().toISOString(), t_mono: monoNow() };
  preAnswer.push({ kind, event: { ...event, ...at } });
}

/** Record the queued events whose group the answer turned on; forget the rest. */
function replayPreAnswer() {
  const queued = preAnswer;
  const overflow = preAnswerOverflow;
  preAnswer = [];
  preAnswerOverflow = {};
  for (const { kind, event } of queued) {
    if (kind === "text") recordTextEvent(event);
    else if (kind === "legacy") recordLegacyEvent(event);
    else recordTraceEvent(event);
  }
  const lost = {};
  for (const [kind, byFamily] of Object.entries(overflow)) {
    for (const [family, n] of Object.entries(byFamily)) {
      // A legacy event would have been recorded with the text group off; a group
      // event with its own group on.
      const kept =
        kind === "legacy"
          ? !telemetryGroupOn("text")
          : Object.hasOwn(FAMILY_GROUP, family) && telemetryGroupOn(FAMILY_GROUP[family]);
      if (kept) lost[family] = (lost[family] || 0) + n;
    }
  }
  preAnswerDropped = Object.keys(lost).length > 0 ? lost : null;
}

let replayDeferred = false;

/** Record both pre-answer queues: the queued group events, then the held intents. */
function replayQueued() {
  replayPreAnswer();
  replayHeldIntents();
}

/**
 * Replace the group state; anything not `true` is off. The first call is the first
 * /api/me answer: the events queued before it are recorded or discarded. With
 * `deferReplay`, both pre-answer queues wait for `releasePreAnswer`, so the caller can
 * stamp the visit into the ambient context first (services/telemetry.js). Queued events
 * keep their capture time either way.
 */
export function setTelemetryGroups(next, { deferReplay = false } = {}) {
  const out = {};
  for (const g of TELEMETRY_GROUPS) out[g] = next?.[g] === true;
  groups = Object.freeze(out);
  if (answered) return;
  answered = true;
  if (deferReplay) replayDeferred = true;
  else replayQueued();
}

/** Replay what a deferred first answer held back; a no-op otherwise. */
export function releasePreAnswer() {
  if (!replayDeferred) return;
  replayDeferred = false;
  replayQueued();
}

/**
 * Pre-answer events past the cap whose group the answer turned on, by family, or
 * null; reset by the call (services/telemetry.js reports them as `pre_enable`).
 */
export function takePreAnswerDrops() {
  const out = preAnswerDropped;
  preAnswerDropped = null;
  return out;
}

/** Back to the state before the first answer (tests). */
export function resetTelemetryGroups() {
  groups = OFF;
  answered = false;
  replayDeferred = false;
  preAnswer = [];
  preAnswerOverflow = {};
  preAnswerDropped = null;
}

/** Whether a group's events are captured right now. */
export function telemetryGroupOn(group) {
  return groups[group] === true;
}

/**
 * `/api/me` booleans → group state. A missing P2 field (a server before the P2 kinds) is
 * off: `telemetryVisitsEnabled` is the server's any-P2-group flag, `telemetryAttentionEnabled`
 * the attention sub-flag, `telemetryIntentEnabled` the intent sub-flag.
 */
export function groupsFromMe(me) {
  return {
    text: me?.telemetryTextEnabled === true,
    interaction: me?.telemetryInteractionEnabled === true,
    net: me?.telemetryNetEnabled === true,
    diagnostics: me?.telemetryDiagnosticsEnabled === true,
    visits: me?.telemetryVisitsEnabled === true,
    attention: me?.telemetryAttentionEnabled === true,
    friction: me?.telemetryFrictionEnabled === true,
    intent: me?.telemetryIntentEnabled === true,
  };
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** `s` cut to at most `TEXT_MAX_BYTES` UTF-8 bytes, never mid-character. */
export function capText(s) {
  if (typeof s !== "string") return s ?? null;
  if (s.length * 3 <= TEXT_MAX_BYTES) return s;
  const bytes = encoder.encode(s);
  if (bytes.length <= TEXT_MAX_BYTES) return s;
  // Back off to a character boundary: never cut inside a multi-byte sequence.
  let end = TEXT_MAX_BYTES;
  while (end > 0 && (bytes[end] & 0xc0) === 0x80) end--;
  return decoder.decode(bytes.subarray(0, end));
}

/**
 * A legacy event's text field: omitted (undefined) while the text group carries the
 * full text, else today's clip.
 */
export function legacyText(s) {
  if (telemetryGroupOn("text")) return undefined;
  return typeof s === "string" ? s.slice(0, LEGACY_CLIP) : s;
}

/**
 * Record a legacy domain event the text group supersedes (`ui.chat.send`,
 * `ui.forecast.submit`) only while the group is off, so one turn is never sent in
 * both families; builds and deployments without the group keep it.
 */
export function recordLegacyEvent(event) {
  if (!answered) {
    if (event && typeof event === "object") queuePreAnswer("legacy", event);
    return;
  }
  if (telemetryGroupOn("text")) return;
  recordUiEvent(event);
}

/** The fields of a text-group event that hold the user's text. */
const TEXT_KEYS = ["text", "value"];

/** A copy of `event` with its text fields capped. */
function capTextFields(event) {
  const out = { ...event };
  for (const k of TEXT_KEYS) if (k in out) out[k] = capText(out[k]);
  return out;
}

/**
 * Record a text-group event (`chat.*`, `submit.*`, `text.*`) when the group is on,
 * with its text fields capped. Never throws into the caller.
 */
export function recordTextEvent(event) {
  if (!answered) {
    // Capped before queueing too: the queue is bounded by count, not size.
    if (event && typeof event === "object") queuePreAnswer("text", capTextFields(event));
    return;
  }
  if (!telemetryGroupOn("text")) return;
  try {
    if (!event || !budget.take(familyOf(event.event))) return;
    recordUiEvent(capTextFields(event));
  } catch {
    /* telemetry is best-effort */
  }
}

/**
 * Record a P2 event (`visit.*`, `tab.start`, `attention.*`) in the domain ring when its group
 * is on and the budget allows. They start only after the first /api/me answer.
 */
export function recordP2Event(event) {
  try {
    const family = familyOf(event?.event);
    const group = P2_FAMILY_GROUP[family];
    if (!group || !telemetryGroupOn(group) || !budget.take(family)) return;
    recordUiEvent(event);
  } catch {
    /* telemetry is best-effort */
  }
}

/** sessionStorage key of the intent events captured before any /api/me answer. */
export const PRE_ANSWER_INTENTS_KEY = "sooth.telemetry.preAnswerIntents";
/** Most intent events kept before an answer. */
export const PRE_ANSWER_INTENTS_MAX = 50;

function sessionStore() {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * Hold an intent event until an /api/me answer says whether the group is on. Kept in
 * sessionStorage, not memory: a page that never asks (the copy-link redeem route) hands its
 * intents to the page it navigates to, which records them once its answer turns the group on.
 */
function holdIntent(event) {
  const store = sessionStore();
  if (!store) return;
  try {
    const held = JSON.parse(store.getItem(PRE_ANSWER_INTENTS_KEY) || "[]");
    if (!Array.isArray(held) || held.length >= PRE_ANSWER_INTENTS_MAX) return;
    held.push({ ...event, captured_at: new Date().toISOString() });
    store.setItem(PRE_ANSWER_INTENTS_KEY, JSON.stringify(held));
  } catch {
    /* best effort */
  }
}

/** Record the held intent events when the answer turned the intent group on; drop them. */
function replayHeldIntents() {
  const store = sessionStore();
  if (!store) return;
  let held = [];
  try {
    held = JSON.parse(store.getItem(PRE_ANSWER_INTENTS_KEY) || "[]");
    store.removeItem(PRE_ANSWER_INTENTS_KEY);
  } catch {
    return;
  }
  if (!Array.isArray(held) || !telemetryGroupOn("intent")) return;
  for (const e of held) if (e && typeof e === "object") recordIntentEvent(e);
}

/**
 * Record an intent event (`intent.<name>`, `intent.<name>.end`, or a point intent such as
 * `forecast.result_seen`) in the domain ring when the intent group is on and the budget
 * allows; before the first /api/me answer it is held (`holdIntent`).
 */
export function recordIntentEvent(event) {
  try {
    if (!event) return;
    if (!answered) {
      holdIntent(event);
      return;
    }
    if (!telemetryGroupOn("intent") || !budget.take("intent")) return;
    recordUiEvent(event);
  } catch {
    /* telemetry is best-effort */
  }
}

/**
 * Record a trace-ring event (`action` / `key` / `scroll` / `viewport` / `net` /
 * `stream` / `store` / `console` / `friction`) when its group is on and the budget allows.
 */
export function recordTraceEvent(event) {
  try {
    const family = familyOf(event?.event);
    const group = FAMILY_GROUP[family];
    if (!group || group === "text") return;
    if (!answered) {
      queuePreAnswer("trace", event);
      return;
    }
    if (!telemetryGroupOn(group) || !budget.take(family)) return;
    traceRing.push(event);
  } catch {
    /* telemetry is best-effort */
  }
}
